import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CssBaseline, ThemeProvider, createTheme, Box, CircularProgress , Typography, } from '@mui/material';
import { ApolloClient, InMemoryCache, ApolloProvider, gql, createHttpLink, useLazyQuery, useMutation} from '@apollo/client';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'; 
import { PageName } from './types'; 
import LoginPage from './LoginPage';
import LandingPage from './LandingPage'; 
import OnboardingPage from './OnboardingPage';
import ActionPopup from './ActionPopup';
import TenantForm from './TenantForm';
import SuccessToast from './SuccessToast';
import UserManagementPage from './SideBarComponents/UserManagement/UserManagementPage';
import DataSourcesPage from './SideBarComponents/DataSources/DataSourcesPage';
import AddDataSourcesPage from './SideBarComponents/DataSources/AddDataSourcesPage'; 
import EditDataSourcesPage from './SideBarComponents/DataSources/EditDataSourcesPage';
import CostAnalysis from './SideBarComponents/CostAnalysis/CostAnalysis';
import KubernetesAnalysis from './SideBarComponents/KubernetesAnalysis/KubernetesAnalysis';
import Dashboard from './Dashboard';
import TroubleshootingPage from './SideBarComponents/Troubleshooting/TroubleshootingPage';
import 'primereact/resources/themes/saga-blue/theme.css'; 
import 'primereact/resources/primereact.min.css';
import InvitationPendingPage from './InvitationPendingPage'; 
import BudgetAndAlerts from './SideBarComponents/Budget&Alerts/Budget&Alerts';
import EditBudgetPage from './SideBarComponents/Budget&Alerts/EditBudgetPage';
import AddBudgetDialog from './SideBarComponents/Budget&Alerts/AddBudgetDialog';
import TotalBudgetsPage from './SideBarComponents/Budget&Alerts/TotalBudgetsPage';
import { CostAnalysisProvider } from './SideBarComponents/CostAnalysis/CostAnalysisContext';
import { useCostAnalysisContext } from './SideBarComponents/CostAnalysis/CostAnalysisContext';
import { KubernetesAnalysisProvider, useKubernetesAnalysisContext } from './SideBarComponents/KubernetesAnalysis/KubernetesAnalysisContext';
import LLMObservability from './SideBarComponents/LLMObservability/LLMObservability';
import MaintenancePage from './MaintenancePage';
// import TraceDetails from './SideBarComponents/LLMObservability/TraceDetails';



const theme = createTheme({
  palette: {
    primary: {
      main: '#4285F4',
    },
    background: {
      default: '#F9FAFB',
    },
  },
  typography: {
    fontFamily: "'Inter'", 
  },
  shape: {
    borderRadius: 4,
  },
});

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const client = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'network-only', errorPolicy: 'all' },
    query: { fetchPolicy: 'network-only', errorPolicy: 'all' },
    mutate: { errorPolicy: 'all' }
  },
});

export interface TenantMembershipUserStub {
  id: string;
  name?: string | null;
  __typename?: string;
}

export interface TenantMembership {
  id: string;
  roleInTenant: string; 
  addedAt: string; 
  user: TenantMembershipUserStub;
  addedBy: TenantMembershipUserStub;
  tenant?: Pick<Tenant, 'id' | 'name'>; 
  __typename?: string;
}

export interface Tenant { 
  id: string;
  name: string;
  alias?: string | null;
  tags?: any | null;
  userMemberships?: TenantMembership[]; 
  dataSourceCount?: number;
  allDataSourceCount?: number;
  __typename?: string;
}

interface User {
  id: string;
  email: string;
  name?: string | null;
   photo?: string | null;
  isFirstLogin?: boolean;
  role?: string;
  tenants?: Pick<Tenant, 'id' | 'name' | 'alias'>[]; 
  tenantMemberships?: TenantMembership[]; 
  __typename?: string;
}

const ACCEPT_TENANT_INVITATION_APP = gql`
   mutation AcceptTenantInvitationApp($token: String!) {
     acceptTenantInvitation(token: $token) {
       id # TenantMembership ID
       tenant {
         id
         name
       }
     }
   }
`;

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      name
      photo
      isFirstLogin
      role
      tenants {
        id
        name
        alias
        dataSourceCount # Added dataSourceCount
        allDataSourceCount
      }
      tenantMemberships {
        roleInTenant
        tenant {
          id
        }
      }
    }
  }
`;

const FINISH_USER_ONBOARDING = gql`
  mutation MarkOnboardingComplete {
    finishUserOnboarding { id isFirstLogin }
  }
`;

const GET_TENANTS = gql`
  query GetCurrentTenants {
    myTenants {
        id
        name
        alias 
        dataSourceCount
        allDataSourceCount
      }
    }
`;

const CHECK_INVITATION_PENDING = gql`
  query CheckInvitationPending {
    isInvitationPending
  }
`;

const RESEND_INVITATION = gql`
  mutation ResendInvitation {
    resendInvitationEmail
  }
`;

const AppContent: React.FC = () => {

  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [showInvitationPending, setShowInvitationPending] = useState<boolean>(false); 
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isTenantFormOpen, setIsTenantFormOpen] = useState(false);
  const [tenantSuccessToastOpen, setTenantSuccessToastOpen] = useState(false);
  const [tenantSuccessToastMessage, setTenantSuccessToastMessage] = useState('');
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [currentUserRoleInTenant, setCurrentUserRoleInTenant] = useState<string | null>(null);
  const [isLoadingTenants, setIsLoadingTenants] = useState<boolean>(true); 
  const { preloadCostAnalysisData, clearPreloadedData } = useCostAnalysisContext(); 
  const { preloadK8sData, clearK8sPreloadedData } = useKubernetesAnalysisContext();
  const preloadedTenantIdRef = useRef<string | undefined>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
      if (currentTenant && currentTenant.id) {
        localStorage.setItem('currentTenantId', currentTenant.id);
      }
  }, [currentTenant]); 

useEffect(() => {
    const tenantId = currentTenant?.id;
    const dataSourceCount = currentTenant?.dataSourceCount;

    if (tenantId && tenantId !== preloadedTenantIdRef.current) {
      console.log(`[App.tsx] Tenant changed to ${tenantId}. Clearing old data and preloading new data.`);
      clearPreloadedData();
      clearK8sPreloadedData();

      if (dataSourceCount && dataSourceCount > 0) {
        preloadCostAnalysisData(tenantId);
        preloadK8sData(tenantId);
      } else {
        console.log(`[App.tsx] Tenant ${tenantId} has no data sources. Skipping all preloads.`);
      }
      
      preloadedTenantIdRef.current = tenantId;
    }
}, [currentTenant, clearPreloadedData, preloadCostAnalysisData, clearK8sPreloadedData, preloadK8sData]);

  const [acceptInvitationApp, { loading: acceptInvitationLoading }] = useMutation(ACCEPT_TENANT_INVITATION_APP, {
    onCompleted: (data) => {
      const tenantName = data.acceptTenantInvitation?.tenant?.name;
      setTenantSuccessToastMessage(`Successfully joined tenant "${tenantName || 'the tenant'}".`);
      setTenantSuccessToastOpen(true);
      sessionStorage.removeItem('pendingInvitationToken');
      checkAuthStatus();
    },
    onError: (error) => {
      setTenantSuccessToastMessage(`Failed to accept invitation: ${error.message}`);
      setTenantSuccessToastOpen(true);
      sessionStorage.removeItem('pendingInvitationToken');
      checkAuthStatus();
    }
  });

   const [resendInvitation, { loading: resendingInvitation }] = useMutation(RESEND_INVITATION, {
    onCompleted: () => {
      setTenantSuccessToastMessage("Invitation has been resent. Please check your email.");
      setTenantSuccessToastOpen(true);
    },
    onError: (error) => {
      setTenantSuccessToastMessage(`Error: ${error.message}`);
      setTenantSuccessToastOpen(true);
    }
  });

  const [checkInvitation] = useLazyQuery(CHECK_INVITATION_PENDING, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.isInvitationPending) {
        setShowInvitationPending(true);
      }
    }
  });

  const [
    checkAuthStatus,
    { loading: isLoadingAuth, data: _authData, error: authError, called: authCalled }
  ] = useLazyQuery<{ me: User | null }>(GET_CURRENT_USER, {
    onCompleted: (data) => {

      if (data && data.me) {
        const fetchedUser = data.me as User;
        setIsLoggedIn(true);
        setUser(fetchedUser);

        const storedTenantId = localStorage.getItem('currentTenantId');
        let tenantToUse: Tenant | null | undefined = null;

        if (!fetchedUser.tenants || fetchedUser.tenants.length === 0) {
          checkInvitation();
        } else {
            setShowInvitationPending(false);
        }

        const pendingToken = sessionStorage.getItem('pendingInvitationToken');
        if (pendingToken) {
          acceptInvitationApp({ variables: { token: pendingToken } });
        }
        if (storedTenantId) {
          tenantToUse = fetchedUser.tenants?.find(t => t.id === storedTenantId);
        }
        if (!tenantToUse && currentTenant?.id) { 
          tenantToUse = fetchedUser.tenants?.find(t => t.id === currentTenant.id);
        }
        if (!tenantToUse) {
          tenantToUse = fetchedUser.tenants && fetchedUser.tenants.length > 0 ? (fetchedUser.tenants[0] as Tenant) : null;
        }
        if (tenantToUse) {
          setAllTenants(fetchedUser.tenants as Tenant[]);
          setCurrentTenant(tenantToUse as Tenant);
          const membership = fetchedUser.tenantMemberships?.find(
            (m) => m.tenant?.id === (tenantToUse as Tenant).id
          );
          setCurrentUserRoleInTenant(membership?.roleInTenant || null);
        } else {
          fetchTenants(); 
          setAllTenants([]);
          setCurrentTenant(null);
          setCurrentUserRoleInTenant(null);
        }
      } else {
        setIsLoggedIn(false);
        setUser(null);
        setAllTenants([]);
        setCurrentTenant(null);
        setCurrentUserRoleInTenant(null);
        localStorage.removeItem('currentTenantId'); 
      }
      setIsLoadingTenants(false);
    },
    onError: (error) => {
      setIsLoggedIn(false);
      setUser(null);
      setAllTenants([]);
      setCurrentTenant(null);
      setCurrentUserRoleInTenant(null);
      setIsLoadingTenants(false);
    }
  });

  useEffect(() => {
    const ADMIN_ONLY_PAGES: PageName[] = ['user-management'];
    const currentPage = location.pathname.split('/')[1] as PageName;
    const isOnAdminPage = ADMIN_ONLY_PAGES.includes(currentPage);
    if (isOnAdminPage && currentUserRoleInTenant && currentUserRoleInTenant !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUserRoleInTenant, location.pathname, navigate]);

  const [
    finishOnboarding,
     { loading: isOnboardingUpdating, error: _onboardingUpdateError }
  ] = useMutation<{ finishUserOnboarding: { id: string; isFirstLogin: boolean } }>(FINISH_USER_ONBOARDING, {
    onCompleted: (data) => {
      if (data && data.finishUserOnboarding) {
        setUser(prevUser =>
          prevUser ? { ...prevUser, isFirstLogin: data.finishUserOnboarding.isFirstLogin } : null
        );
        setIsPopupVisible(true);
      } else {
        setIsPopupVisible(true); 
      }
    },
    onError: (error) => {
      setIsPopupVisible(true); 
    }
  });

  const [fetchTenants, { loading: tenantsLoadingQueryActive, error: tenantsError }] = useLazyQuery<{ myTenants: Tenant[] }>(GET_TENANTS, {
    onCompleted: (data) => {
      if (data && data.myTenants) {
        const fetchedTenants = data.myTenants as Tenant[];
        setAllTenants(fetchedTenants);
        if (!currentTenant && fetchedTenants.length > 0) {
          const newTenant = fetchedTenants[0] as Tenant;
          setCurrentTenant(newTenant);
          if(user?.tenantMemberships) {
            const membership = user.tenantMemberships.find(m => m.tenant?.id === newTenant.id);
            setCurrentUserRoleInTenant(membership?.roleInTenant || null);
          }
        }
      }
      setIsLoadingTenants(false);
    },
    onError: (error) => {
      setIsLoadingTenants(false);
    }
  });

  useEffect(() => {
    if (!authCalled || authError) {
        checkAuthStatus();
    }
  }, [checkAuthStatus, authCalled, authError]);

  const handleLoginSuccess = useCallback(() => {
    console.log("App.tsx: handleLoginSuccess called, calling checkAuthStatus.");
    checkAuthStatus();
  }, [checkAuthStatus]);

    const handleResendInvitation = () => {
      resendInvitation();
  };

  const handleOnboardingContinue = async () => {
    if (user) { finishOnboarding(); } else { setIsPopupVisible(true); }
  };
  const handlePopupConfirm = () => {
    setIsPopupVisible(false);
    if (user && !user.isFirstLogin && allTenants.length === 0 && !currentTenant) {
      handleOpenTenantForm();
    }
  };
  const handlePopupClose = () => {
    setIsPopupVisible(false);
     if (user && !user.isFirstLogin && allTenants.length === 0 && !currentTenant) {
      handleOpenTenantForm();
    }
  };
  const handleOpenTenantForm = () => setIsTenantFormOpen(true);
  const handleCloseTenantForm = () => setIsTenantFormOpen(false);
  const handleTenantCreated = (newTenant: Tenant) => {
    setTenantSuccessToastMessage(`Tenant "${newTenant.name}" added successfully`);
    setTenantSuccessToastOpen(true);
    handleCloseTenantForm();
    checkAuthStatus();
  };
  const handleCloseTenantSuccessToast = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setTenantSuccessToastOpen(false);
  };
  const handleChangeTenant = (tenantId: string) => {
    const selectedTenant = allTenants.find(t => t.id === tenantId); 
    if (selectedTenant) {
      if (currentTenant?.id === selectedTenant.id) return; 
      setCurrentTenant(selectedTenant); 
      if (user?.tenantMemberships) {
        const membership = user.tenantMemberships.find((m) => m.tenant?.id === selectedTenant.id);
        setCurrentUserRoleInTenant(membership?.roleInTenant || null);
      }
      setTenantSuccessToastMessage(`Tenant switched to ${selectedTenant.name}`);
      setTenantSuccessToastOpen(true);
    }
  };
  const handleTenantDeleted = (deletedTenantId: string) => {
    const newTenantList = allTenants.filter(tenant => tenant.id !== deletedTenantId);
    setAllTenants(newTenantList);
    if (currentTenant?.id === deletedTenantId) {
      const nextTenant = newTenantList.length > 0 ? newTenantList[0] : null;
      setCurrentTenant(nextTenant);
      if (nextTenant && user?.tenantMemberships) {
        const membership = user.tenantMemberships.find(m => m.tenant?.id === nextTenant.id);
        setCurrentUserRoleInTenant(membership?.roleInTenant || null);
      } else if (!nextTenant) {
        setCurrentUserRoleInTenant(null);
      }
    }
  };

  const handleLogout = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    try {
      await fetch(`${BACKEND_URL}/auth/logout`, { method: 'GET', credentials: 'include' });
    } catch (error) {
    } finally {
      setIsLoggedIn(false);
      setUser(null);
      setIsPopupVisible(false);
      setTenantSuccessToastOpen(false);
      setAllTenants([]);
      setCurrentTenant(null);
      setCurrentUserRoleInTenant(null);
      setIsLoadingTenants(true); 
      await client.resetStore(); 
    }
  };

  if (isLoggedIn === null && (isLoadingAuth || !authCalled)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (location.pathname === '/') {
    return <LandingPage />;
  }

  const showOnboarding = !!(isLoggedIn && user && user.isFirstLogin === true);
  const isUIBlurred = showOnboarding || isPopupVisible || isOnboardingUpdating || isTenantFormOpen;

  // const getActivePageFromLocation = (): PageName => {
  //   const path = location.pathname;
  //   if (path.startsWith('/dashboard')) return 'dashboard';
  //   if (path.startsWith('/budget-alerts') || path.startsWith('/budget-alerts/add')) return 'budget-alerts'; 
  //   if (path.startsWith('/data-sources') || path.startsWith('/data-sources/add')) return 'data-sources'; 
  //   if (path.startsWith('/user-management')) return 'user-management';
  //   if (path.startsWith('/cost-analysis')) return 'cost-analysis';
  //   if (path.startsWith('/kubernetes-analysis')) return 'kubernetes-analysis';
  //   if (path.startsWith('/budget-alerts')) return 'budget-alerts';
  //   if (path.startsWith('/llm-observability')) return 'llm-observability';
  //   if (path.startsWith('/settings')) return 'settings';
  //   if (path.startsWith('/copilot')) return 'copilot';
    
  //   return 'dashboard'; 
  // };
  // const activePage = getActivePageFromLocation();

  const getActivePageFromLocation = (): PageName => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/budget-alerts') || path.startsWith('/budget-alerts/add')) return 'budget-alerts'; 
    if (path.startsWith('/data-sources') || path.startsWith('/data-sources/add')) return 'data-sources'; 
    if (path.startsWith('/user-management')) return 'user-management';
    if (path.startsWith('/cost-analysis')) return 'cost-analysis';
    if (path.startsWith('/kubernetes-analysis')) return 'kubernetes-analysis';
    if (path.startsWith('/budget-alerts')) return 'budget-alerts';
    if (path.startsWith('/llm-observability')) {
      // NEW: Check for the nested trace path first
      if (path.startsWith('/llm-observability/traces')) 
      return 'llm-observability-traces'
      return 'llm-observability';
    }
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/copilot')) return 'copilot';
    return 'dashboard'; 
  };
  const activePage = getActivePageFromLocation();


  return (
    <>
      {!isLoggedIn ? <LoginPage onLoginSuccess={handleLoginSuccess} /> : (
        <Box sx={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
          
          {showInvitationPending ? (
                <InvitationPendingPage 
                    onResendInvitation={handleResendInvitation}
                    resending={resendingInvitation}
                    userEmail={user?.email}
                />
            ) : (
                <>
          <React.Suspense fallback={
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 68px)">
              <CircularProgress />
            </Box>
          }>

          <Routes>            
          <Route 
              path="/*" 
              element={<Dashboard 
                isBlurred={isUIBlurred}
                onLogout={handleLogout}
                onAddNewTenant={handleOpenTenantForm}
                currentTenant={currentTenant}
                allTenants={allTenants}
                isLoadingTenants={isLoadingTenants || tenantsLoadingQueryActive}
                onChangeTenant={handleChangeTenant}
                onTenantDeleted={handleTenantDeleted}
                activePage={activePage}
                userRole={currentUserRoleInTenant}
                tenantMemberships={user?.tenantMemberships}
                userName={user?.name}
                userEmail={user?.email}
                userProfilePicture={user?.photo}
              />}>

            <Route 
                path="dashboard" 
                element={
                    <Box sx={{
                        position: 'relative',
                        p: { xs: 2, sm: 3 },
                    }} />
                } 
              />
              <Route path="data-sources" element={<DataSourcesPage currentTenant={currentTenant} />} />
              <Route path="data-sources/add" element={<AddDataSourcesPage />}/>
              <Route path="data-sources/edit/:dataSourceId" element={<EditDataSourcesPage />} />
              <Route path="user-management" element={
                        <UserManagementPage currentTenantId={currentTenant?.id || null} />
                } />
                <Route path="copilot" element={<TroubleshootingPage />} />
                <Route path="cost-analysis" element={
                    <Box>
                        <CostAnalysis 
                        key={currentTenant?.id || 'no-tenant'}
                        tenantId={currentTenant?.id || ''}
                        dataSourceCount={currentTenant?.dataSourceCount} 
                        />
                    </Box>
                } />
                <Route path="kubernetes-analysis" element={
                  <Box>

                       <KubernetesAnalysis 
                          key={currentTenant?.id || 'no-tenant'} 
                          tenantId={currentTenant?.id || ''}
                          dataSourceCount={currentTenant?.dataSourceCount} 
                          userRole={currentUserRoleInTenant}
                      /> 
                  </Box>
              } />

            <Route path="budget-alerts" element={<BudgetAndAlerts />}>
                <Route index element={<TotalBudgetsPage />} />              
                <Route path="edit/:budgetId" element={<EditBudgetPage />} />              
            </Route>
            <Route path="budget-alerts/add" element={<AddBudgetDialog />} />  

            {/* <Route path="llm-observability/traces" element={<TraceDetails tenantId={currentTenant?.id} />}/> */}
                      <Route 
             path="llm-observability" 
             element={<LLMObservability tenantId={currentTenant?.id} />}
          />        
          
          {/* <Route 
          path="llm-observability" 
          //  element={<LLMObservability/>}/>
          element={<LLMObservability tenantId={currentTenant?.id} />}/>
          <Route path="llm-observability/traces" element={<TraceDetails tenantId={currentTenant?.id} />}/> */}

          <Route path="settings" element={
                    <Box sx={{ backgroundColor: 'white', borderRadius: '10px', p: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
                        <Typography>Settings Page Content</Typography>
                    </Box>
                } />
                <Route index element={<Navigate to="dashboard" replace />} />
            </Route>
          </Routes>
          </React.Suspense> 
              
          {showOnboarding && (
            <OnboardingPage onOnboardingComplete={handleOnboardingContinue} />
          )}
          {isPopupVisible && (
            <ActionPopup onClose={handlePopupClose} onConfirm={handlePopupConfirm} />
          )}
          {isTenantFormOpen && (
            <TenantForm
              open={isTenantFormOpen}
              onClose={handleCloseTenantForm}
              onTenantCreated={handleTenantCreated}
              allTenants={allTenants}
            />
          )}
          </>
          )}
          <SuccessToast
            open={tenantSuccessToastOpen}
            message={tenantSuccessToastMessage}
            onClose={handleCloseTenantSuccessToast}
          />
        </Box>
      )}
    </>
  );
};

const App: React.FC = () => {
const isMaintenanceMode = process.env.REACT_APP_MAINTAINANCE_PAGE === 'true';
return (
    <ApolloProvider client={client}>
      <CostAnalysisProvider>
        <KubernetesAnalysisProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route 
                  path="/login" 
                  element={
                    isMaintenanceMode ? (
                      <MaintenancePage /> 
                    ) : (
                      <AppContent /> 
                    )
                  } 
                />
                <Route 
                  path="/*" 
                  element={
                    isMaintenanceMode ? (
                      <Navigate to="/login" replace /> 
                    ) : (
                      <AppContent />
                    )
                  } 
                />
              </Routes>
            </Router>
          </ThemeProvider>
        </KubernetesAnalysisProvider>
      </CostAnalysisProvider>
    </ApolloProvider>
  );
};

export default App;


